<template>
  <div class="bug-modal-overlay" @click="$emit('close-modal')">
    <div class="bug-modal screenshot-review-modal" @click.stop>
      <div class="bug-modal-header">
        <h3>Review Your Bug Report</h3>
        <button @click="$emit('close-modal')" class="close-btn">&times;</button>
      </div>
      
      <div class="bug-modal-body">
        <div v-if="isCapturing" class="capturing-state">
          <div class="spinner"></div>
          <p>Capturing screenshot...</p>
        </div>
        
        <div v-else class="review-content">
          <!-- Bug Report Summary -->
          <div class="bug-summary">
            <h4>Bug Report Summary</h4>
            <div class="summary-grid">
              <div class="summary-item">
                <strong>Priority:</strong>
                <span class="priority-badge" :class="bugReport.priority">
                  {{ bugReport.priority }}
                </span>
              </div>
              <div class="summary-item full-width">
                <strong>Description:</strong>
                <p>{{ bugReport.description || 'No description provided' }}</p>
              </div>
              <div class="summary-item full-width" v-if="bugReport.steps">
                <strong>Steps to reproduce:</strong>
                <p>{{ bugReport.steps }}</p>
              </div>
            </div>
          </div>

          <!-- Screenshot Section -->
          <div class="screenshot-section">
            <h4>Screenshot</h4>
            
            <div v-if="displayScreenshot" class="screenshot-container">
              <div class="screenshot-wrapper">
                <img 
                  :src="displayScreenshot" 
                  alt="Bug report screenshot" 
                  class="screenshot-image"
                />
                
                <!-- Screenshot Actions -->
                <div class="screenshot-actions">
                  <button @click="$emit('edit-screenshot')" class="action-btn edit">
                    <v-icon size="16">mdi-pencil</v-icon>
                    {{ finalScreenshot ? 'Edit Again' : 'Edit & Annotate' }}
                  </button>
                  <button @click="recaptureScreenshot" class="action-btn recapture">
                    <v-icon size="16">mdi-camera-retake</v-icon>
                    Retake Screenshot
                  </button>
                </div>
              </div>
              
              <div class="screenshot-info">
                <div class="info-item">
                  <v-icon size="16">mdi-check-circle</v-icon>
                  <span v-if="finalScreenshot">Annotated screenshot ready for submission</span>
                  <span v-else>Full page screenshot captured</span>
                </div>
                <div class="info-item" v-if="!finalScreenshot">
                  <v-icon size="16">mdi-palette</v-icon>
                  <span>You can highlight important areas or hide sensitive information</span>
                </div>
                <div class="info-item" v-else>
                  <v-icon size="16">mdi-shield-check</v-icon>
                  <span>Screenshot includes your annotations and hidden sensitive data</span>
                </div>
              </div>
            </div>
            
            <div v-else class="no-screenshot">
              <div class="no-screenshot-content">
                <v-icon size="48">mdi-camera-off</v-icon>
                <h5>No Screenshot Available</h5>
                <p>Screenshot capture failed, but you can still submit your bug report.</p>
                <button @click="retryScreenshot" class="action-btn retry">
                  <v-icon size="16">mdi-camera</v-icon>
                  Try Again
                </button>
              </div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="modal-actions">
            <button @click="$emit('back-to-form')" class="action-btn secondary">
              <v-icon size="16">mdi-arrow-left</v-icon>
              Back to Edit
            </button>
            
            <button @click="$emit('submit-final-report')" class="action-btn primary" :disabled="isSubmitting">
              <v-icon size="16" v-if="!isSubmitting">mdi-send</v-icon>
              <div v-else class="button-spinner"></div>
              {{ isSubmitting ? 'Sending...' : 'Send Bug Report' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ScreenshotReviewModal',
  
  props: {
    bugReport: {
      type: Object,
      required: true
    },
    capturedScreenshot: {
      type: String,
      default: null
    },
    finalScreenshot: {
      type: String,
      default: null
    },
    isCapturing: {
      type: Boolean,
      default: false
    },
    isSubmitting: {
      type: Boolean,
      default: false
    }
  },
  
  emits: ['close-modal', 'back-to-form', 'edit-screenshot', 'submit-final-report', 'recapture-screenshot'],
  
  computed: {
    displayScreenshot() {
      // Show edited screenshot if available, otherwise show original
      return this.finalScreenshot || this.capturedScreenshot
    }
  },
  
  methods: {
    recaptureScreenshot() {
      this.$emit('recapture-screenshot')
    },
    
    retryScreenshot() {
      this.$emit('recapture-screenshot')
    }
  }
}
</script>

<style scoped>
.bug-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
}

.screenshot-review-modal {
  background: white;
  border-radius: 12px;
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
}

.bug-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 24px 0;
  border-bottom: 1px solid #e5e7eb;
}

.bug-modal-header h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #111827;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #6b7280;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
}

.close-btn:hover {
  background: #f3f4f6;
  color: #374151;
}

.bug-modal-body {
  padding: 24px;
}

.capturing-state {
  text-align: center;
  padding: 40px 20px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e5e7eb;
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.review-content {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

/* Bug Summary Styles */
.bug-summary h4,
.screenshot-section h4 {
  margin: 0 0 16px 0;
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 8px;
}

.summary-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  background: #f8fafc;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.summary-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.summary-item.full-width {
  grid-column: 1 / -1;
}

.summary-item strong {
  font-size: 14px;
  color: #374151;
  font-weight: 600;
}

.summary-item span,
.summary-item p {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  line-height: 1.5;
}

.priority-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
}

.priority-badge.low {
  background: #d1fae5;
  color: #065f46;
}

.priority-badge.medium {
  background: #fef3c7;
  color: #92400e;
}

.priority-badge.high {
  background: #fee2e2;
  color: #991b1b;
}

.priority-badge.critical {
  background: #fecaca;
  color: #7f1d1d;
}

/* Screenshot Styles */
.screenshot-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.screenshot-wrapper {
  position: relative;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  background: white;
}

.screenshot-image {
  width: 100%;
  height: auto;
  max-height: 400px;
  object-fit: contain;
  display: block;
}

.screenshot-actions {
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  gap: 8px;
}

.screenshot-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #6b7280;
  font-size: 14px;
}

.info-item .v-icon {
  color: #10b981;
}

/* No Screenshot Styles */
.no-screenshot {
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  padding: 40px 20px;
}

.no-screenshot-content {
  text-align: center;
  color: #6b7280;
}

.no-screenshot-content .v-icon {
  color: #9ca3af;
  margin-bottom: 16px;
}

.no-screenshot-content h5 {
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 600;
  color: #374151;
}

.no-screenshot-content p {
  margin: 0 0 20px 0;
  font-size: 14px;
}

/* Action Buttons */
.action-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  background: white;
}

.action-btn.secondary {
  color: #6b7280;
  border-color: #d1d5db;
}

.action-btn.secondary:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}

.action-btn.primary {
  background: #3b82f6;
  color: white;
  border-color: #3b82f6;
}

.action-btn.primary:hover:not(:disabled) {
  background: #2563eb;
  border-color: #2563eb;
}

.action-btn.edit {
  background: #10b981;
  color: white;
  border-color: #10b981;
}

.action-btn.edit:hover {
  background: #059669;
  border-color: #059669;
}

.action-btn.recapture,
.action-btn.retry {
  background: #f59e0b;
  color: white;
  border-color: #f59e0b;
}

.action-btn.recapture:hover,
.action-btn.retry:hover {
  background: #d97706;
  border-color: #d97706;
}

.action-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.modal-actions {
  display: flex;
  gap: 16px;
  justify-content: space-between;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
}

.button-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@media (max-width: 768px) {
  .summary-grid {
    grid-template-columns: 1fr;
  }
  
  .screenshot-actions {
    position: static;
    margin-top: 12px;
    justify-content: center;
  }
  
  .modal-actions {
    flex-direction: column;
  }
  
  .action-btn {
    justify-content: center;
  }
}
</style>